import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { GeneralSettingsData, ProfessionalData, Availability } from '../../../../models';
import { getProfessionalAvailabilityAsync } from '../../../../ajax/requests';
import { isCancelledError } from '../../../../ajax/errors';
import { alertError } from '../../../../store/alerts/actions';
import { parseTimeStringToMinutes, minutesToTimeString } from '../../../../shared/utils';
import { AutocompleteTextField, ComboField, ComboFieldOption } from '../../../../components/Forms/fields';
import AvailabilityCalendar from './AvailabilityCalendar';
import ConfirmSlotBar from './ConfirmSlotBar';

const MONTH_COUNT = 5;

export interface AvailabilityCheckerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSlotConfirmed: (data: any) => void;
    settings: GeneralSettingsData;
    professionals: ProfessionalData[];
    selectedBranch: string | null;
}

const SERVICE_PREFIX = 's:';
const PROFESSIONAL_PREFIX = 'p:';

const FiltersRow = styled.div`
    display: flex;
    gap: var(--spacer-M);
    margin-bottom: var(--spacer-M);
    align-items: flex-end;
`;

const PeriodField = styled.div`
    width: 160px;
    flex-shrink: 0;
`;

const StatusText = styled.p`
    color: var(--text-muted);
    margin: 0;
`;

const AvailabilityCheckerModal: React.FC<AvailabilityCheckerModalProps> = ({
    isOpen,
    onClose,
    onSlotConfirmed,
    settings,
    professionals,
    selectedBranch,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();

    const [selectedService, setSelectedService] = useState<string | undefined>(undefined);
    const [selectedProfessionalId, setSelectedProfessionalId] = useState<string | undefined>(undefined);
    const [selectedMonth, setSelectedMonth] = useState(() => moment().format('YYYY-MM'));
    const [availability, setAvailability] = useState<Availability[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<Availability | undefined>(undefined);

    const filterOptions = useMemo(() => [
        ...Object.keys(settings.services ?? {}).map(k => SERVICE_PREFIX + k),
        ...professionals.map(p => PROFESSIONAL_PREFIX + p.id),
    ], [settings.services, professionals]);

    const professionalMap = useMemo(
        () => new Map(professionals.map(p => [p.id, p.name])),
        [professionals],
    );

    const filterLabelOf = (option: string): string => {
        if (option.startsWith(SERVICE_PREFIX)) {
            const key = option.slice(SERVICE_PREFIX.length);
            return settings.services?.[key]?.name ?? key;
        }
        const id = option.slice(PROFESSIONAL_PREFIX.length);
        return professionalMap.get(id) ?? id;
    };

    const filterGroupOf = (option: string): string =>
        option.startsWith(SERVICE_PREFIX) ? t(TK.service) : t(TK.professional);

    const filterValue = selectedService
        ? SERVICE_PREFIX + selectedService
        : selectedProfessionalId
            ? PROFESSIONAL_PREFIX + selectedProfessionalId
            : '';

    const handleFilterChange = (value: string) => {
        if (value.startsWith(SERVICE_PREFIX)) {
            setSelectedService(value.slice(SERVICE_PREFIX.length));
            setSelectedProfessionalId(undefined);
        } else if (value.startsWith(PROFESSIONAL_PREFIX)) {
            setSelectedProfessionalId(value.slice(PROFESSIONAL_PREFIX.length));
            setSelectedService(undefined);
        } else {
            setSelectedService(undefined);
            setSelectedProfessionalId(undefined);
        }
    };

    const monthOptions = useMemo<ComboFieldOption[]>(() => {
        const base = moment().startOf('month');
        return Array.from({ length: MONTH_COUNT }, (_, i) => {
            const m = base.clone().add(i, 'months');
            return { value: m.format('YYYY-MM'), label: m.format('MMMM YYYY') };
        });
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setSelectedService(undefined);
            setSelectedProfessionalId(undefined);
            setAvailability([]);
            setSelectedSlot(undefined);
            setSelectedMonth(moment().format('YYYY-MM'));
        }
    }, [isOpen]);

    useEffect(() => {
        setSelectedSlot(undefined);

        if (!selectedService && !selectedProfessionalId) {
            setAvailability([]);
            return;
        }

        const controller = new AbortController();

        const durationMin = selectedService
            ? (settings.services?.[selectedService]?.defaultDurationMinutes ?? 60)
            : 60;

        const monthStart = moment(selectedMonth, 'YYYY-MM').startOf('month');
        const fromDate = (monthStart.isBefore(moment(), 'day') ? moment() : monthStart).format('YYYY-MM-DDTHH:mm:ss');
        const toDate = moment(selectedMonth, 'YYYY-MM').endOf('month').format('YYYY-MM-DDTHH:mm:ss');

        const fetchForProfessional = (id: string) =>
            getProfessionalAvailabilityAsync(id, selectedService ?? '', durationMin, toDate, fromDate);

        setLoading(true);

        const fetchPromise: Promise<Availability[]> = selectedProfessionalId
            ? fetchForProfessional(selectedProfessionalId)
            : Promise.allSettled(
                professionals.map(p => fetchForProfessional(p.id))
            ).then(results =>
                results.flatMap(r => r.status === 'fulfilled' ? r.value : [])
            );

        fetchPromise
            .then(slots => {
                if (!controller.signal.aborted) {
                    setAvailability(slots);
                }
            })
            .catch(error => {
                if (!isCancelledError(error) && !controller.signal.aborted) {
                    dispatch(alertError(t(TK.somethingWentWrong)));
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [selectedService, selectedProfessionalId, selectedMonth]);

    const now = moment().format('YYYY-MM-DDTHH:mm');
    const futureSlots = useMemo(
        () => availability.filter(s => s.dateTime >= now),
        [availability, now],
    );

    const handleConfirm = () => {
        if (!selectedSlot) return;

        const professional = professionals.find(p => p.id === selectedSlot.professionalId);
        const durationMin = selectedService
            ? (settings.services?.[selectedService]?.defaultDurationMinutes ?? 60)
            : 60;
        const start = moment(selectedSlot.dateTime).format('HH:mm');
        const end = minutesToTimeString(parseTimeStringToMinutes(start) + durationMin);
        const date = moment(selectedSlot.dateTime).format('YYYY-MM-DD');

        onSlotConfirmed({
            type: selectedService,
            date,
            start,
            end,
            professionalId: professional?.id,
            professionalName: professional?.name,
            professionalColor: professional?.colorCode,
            room: selectedSlot.room,
            branchId: selectedBranch,
        });
    };

    const hasFilters = !!(selectedService || selectedProfessionalId);

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="xl">
            <ModalHeader toggle={onClose}>{t(TK.checkAvailability)}</ModalHeader>
            <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <FiltersRow>
                    <PeriodField>
                        <ComboField
                            id="availability-month"
                            label={t(TK.month)}
                            value={selectedMonth}
                            options={monthOptions}
                            onChange={v => setSelectedMonth(v ?? moment().format('YYYY-MM'))}
                        />
                    </PeriodField>
                    <div style={{ flex: 1 }}>
                        <AutocompleteTextField
                            id="availability-filter"
                            label={`${t(TK.service)} / ${t(TK.professional)}`}
                            value={filterValue}
                            options={filterOptions}
                            optionLabelResolver={filterLabelOf}
                            searchTextResolver={filterLabelOf}
                            groupBy={filterGroupOf}
                            onValueChanged={handleFilterChange}
                        />
                    </div>
                </FiltersRow>

                {!hasFilters ? (
                    <StatusText>{t(TK.selectServiceOrProfessional)}</StatusText>
                ) : loading ? (
                    <StatusText>{t(TK.loading)}</StatusText>
                ) : futureSlots.length === 0 ? (
                    <StatusText>{t(TK.noSlotsAvailable)}</StatusText>
                ) : (
                    <AvailabilityCalendar
                        availability={futureSlots}
                        professionals={professionals}
                        selectedSlot={selectedSlot}
                        onSelectSlot={setSelectedSlot}
                    />
                )}

                {selectedSlot && (
                    <ConfirmSlotBar
                        slot={selectedSlot}
                        professionals={professionals}
                        selectedService={selectedService}
                        settings={settings}
                        onConfirm={handleConfirm}
                    />
                )}
            </ModalBody>
        </Modal>
    );
};

export default AvailabilityCheckerModal;
